---
title: Diagnostics
lede: A read-only view of the database, the running processes and the data folder, and where they disagree.
order: 53
part: Help
---

Diagnostics shows what Airlock has stored, what it is running and what it keeps on disk. Use it to find out why the data folder is large, or why a session is not doing what you expect. Open it with `⌘⌥D`, or pick a section by name from the **Debug** menu.

![Diagnostics](media/debug-overview.webp)

| Section | What it shows |
|---|---|
| **Overview** | What disagrees with what |
| **Database** | Every table and row |
| **Processes** | Sandboxes, terminals, tunnels, log tails |
| **Data folder** | `~/.airlock`, area by area |
| **Caches** | Warm images and cached dependency trees |
| **Environment** | Versions, computer, isolation |
| **Window state** | What the window remembers |
| **Graph** | How one session hangs together |

The header holds **Refresh** (read everything again), **Done**, and **Sweep orphans**.

### Overview

It should say *Nothing inconsistent*. Otherwise it lists directories and rows that no session claims any more. **Sweep orphans** deletes them, after a confirmation; it cannot be undone.

### Database

Every table and view, a page at a time. Sort by any column, filter, and open a row. The **QUERY** field runs your own read-only `SELECT`; `⌘↩` runs it.

### Processes

![The processes section](media/debug-processes.webp)

Everything Airlock is running, filtered by kind: sandboxes, terminals, project processes, tunnels and log tails. A process with no session is tagged **NO SESSION**.

### Data folder

![The data folder section](media/debug-disk.webp)

Each area under the [data folder](/data-folder) and its size, then the files at its root. A directory left behind by a deleted session is tagged **ORPHAN**.

### Caches

The **WARM IMAGES** and **CACHED TREES** that make the next session start faster. You can open either to read its contents; opening a warm image boots a throwaway sandbox.

**Start from nothing** › **Empty** removes both caches. Nothing is lost but time: the next session of every workspace installs its dependencies again.

### Environment

The build, Electron and its runtime, the computer, the micro-VM runtime, and the path of each tool Airlock manages. It also reads back `AIRLOCK_HOME` and `AIRLOCK_SANDBOX`, and marks the latter in amber when it forces the local backend.

### Window state

What the window remembers between launches: the workspace and session it was on, pane sizes, the dock, and the tabs and files open per session.

### Graph

![The graph section](media/debug-graph.webp)

One session drawn as its parts: its machine, its directories and rows, its shells and tails, its tunnels. Pick the session under **Session**. Arrow keys pan, plus and minus zoom, `0` fits. Below it, **NOTHING POINTS AT THESE** lists what no session claims.
